import { ref, onMounted } from 'vue'
import type { ShortcodeConfiguration } from '../types/config'

/**
 * Composable to extract and parse shortcode configuration from data-config attribute
 * 
 * @param mountElementId - The ID of the DOM element where the Vue app is mounted
 * @returns Reactive configuration object
 */
export function useConfig(mountElementId: string) {
  const config = ref<ShortcodeConfiguration | null>(null)
  const error = ref<string | null>(null)

  onMounted(() => {
    const element = document.getElementById(mountElementId)
    
    if (!element) {
      error.value = `Mount element not found: ${mountElementId}`
      console.error('[Conflated Domains Config] ' + error.value)
      console.warn(
        '[Conflated Domains Config] Ensure the shortcode rendered correctly and the element ID matches. ' +
        'Expected element with id="' + mountElementId + '"'
      )
      return
    }

    const configJson = element.getAttribute('data-config')
    
    if (!configJson) {
      error.value = 'data-config attribute not found'
      console.error('[Conflated Domains Config] ' + error.value)
      console.warn(
        '[Conflated Domains Config] The shortcode must include a data-config attribute. ' +
        'Check that the shortcode is rendering correctly in: ' + mountElementId
      )
      return
    }

    try {
      const parsed = JSON.parse(configJson)
      
      // Validate required fields
      if (!parsed.instance_id) {
        throw new Error('Missing required field: instance_id')
      }

      // Parse available_tlds if it's a string (shouldn't be, but defensive)
      if (typeof parsed.available_tlds === 'string') {
        console.warn('[Conflated Domains Config] available_tlds was a string, converting to array')
        parsed.available_tlds = parsed.available_tlds.split(',').map((tld: string) => tld.trim())
      }

      // Parse tld_pricing if it's a JSON string
      if (typeof parsed.tld_pricing === 'string') {
        try {
          parsed.tld_pricing = JSON.parse(parsed.tld_pricing)
        } catch (parseError) {
          console.warn('[Conflated Domains Config] Failed to parse tld_pricing JSON string, using empty array:', parseError)
          parsed.tld_pricing = []
        }
      }

      // Ensure tld_pricing is an array
      if (!Array.isArray(parsed.tld_pricing)) {
        console.warn('[Conflated Domains Config] tld_pricing is not an array, converting to empty array')
        parsed.tld_pricing = []
      }

      config.value = parsed as ShortcodeConfiguration
      console.log('[Conflated Domains Config] Configuration loaded successfully for:', mountElementId)
    } catch (e) {
      error.value = `Failed to parse config: ${e instanceof Error ? e.message : 'Unknown error'}`
      console.error('[Conflated Domains Config] ' + error.value, e)
      console.warn(
        '[Conflated Domains Config] JSON parse failed. data-config content:', 
        configJson.substring(0, 200) + (configJson.length > 200 ? '...' : '')
      )
    }
  })

  return { 
    config, 
    error,
    isReady: () => config.value !== null 
  }
}
